import { useState, useRef, useEffect, useLayoutEffect } from 'react';

const TOOLTIP_MAX_WIDTH = 280;
const VIEWPORT_MARGIN = 8;

const KpiTooltip = ({ text, isMobile = false, placement = 'top' }: {
    text: string;
    isMobile?: boolean;
    /** 'top' (default) floats above the icon; 'right' sits beside it — use where there is no room above, e.g. the TopBar. */
    placement?: 'top' | 'right';
}) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, caretLeft: 0, caretTop: 0 });
    const isRight = placement === 'right';
    const iconRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLSpanElement>(null);
    const wrapRef = useRef<HTMLSpanElement>(null);

    const measure = () => {
        if (!iconRef.current) return;
        const r = iconRef.current.getBoundingClientRect();
        if (isRight) {
            setPos({ top: r.top + r.height / 2, left: r.right + 8, caretLeft: 0, caretTop: 0 });
            return;
        }
        const iconCenterX = r.left + r.width / 2;
        const idealLeft = iconCenterX - TOOLTIP_MAX_WIDTH / 2;
        const clampedLeft = Math.max(
            VIEWPORT_MARGIN,
            Math.min(idealLeft, window.innerWidth - TOOLTIP_MAX_WIDTH - VIEWPORT_MARGIN),
        );
        const caretLeft = iconCenterX - clampedLeft;
        setPos({ top: r.top - 8, left: clampedLeft, caretLeft, caretTop: 0 });
    };

    // After tooltip renders, reposition using actual rendered width so short
    // content doesn't leave unused space and the caret stays centred on the icon.
    useLayoutEffect(() => {
        if (!open || !tooltipRef.current || !iconRef.current) return;
        const rect = tooltipRef.current.getBoundingClientRect();
        if (isRight) {
            const ir = iconRef.current.getBoundingClientRect();
            const iconCenterY = ir.top + ir.height / 2;
            let left = ir.right + 8;
            if (left + rect.width > window.innerWidth - VIEWPORT_MARGIN) {
                left = Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.width - VIEWPORT_MARGIN);
            }
            const half = rect.height / 2;
            const top = Math.max(
                VIEWPORT_MARGIN + half,
                Math.min(iconCenterY, window.innerHeight - VIEWPORT_MARGIN - half),
            );
            // Caret is measured from the bubble's own top edge, so it keeps pointing at the
            // icon even when the bubble was clamped away from the icon's centre.
            setPos({ top, left, caretLeft: 0, caretTop: iconCenterY - (top - half) });
            return;
        }
        const actualWidth = rect.width;
        const iconRect = iconRef.current.getBoundingClientRect();
        const iconCenterX = iconRect.left + iconRect.width / 2;
        const idealLeft = iconCenterX - actualWidth / 2;
        const clampedLeft = Math.max(
            VIEWPORT_MARGIN,
            Math.min(idealLeft, window.innerWidth - actualWidth - VIEWPORT_MARGIN),
        );
        setPos({ top: iconRect.top - 8, left: clampedLeft, caretLeft: iconCenterX - clampedLeft, caretTop: 0 });
    }, [open, isRight]);

    const handleMouseEnter = () => { measure(); setOpen(true); };
    const handleMobileClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!open) measure();
        setOpen(v => !v);
    };

    useEffect(() => {
        if (!isMobile || !open) return;
        const close = () => setOpen(false);
        // Close when scrolling, or when tapping anywhere outside the icon/tooltip.
        const handleOutside = (e: Event) => {
            const target = e.target as Node;
            if (wrapperRef.current?.contains(target)) return;
            setOpen(false);
        };
        window.addEventListener('scroll', close, { passive: true, capture: true });
        document.addEventListener('mousedown', handleOutside, true);
        document.addEventListener('touchstart', handleOutside, true);
        return () => {
            window.removeEventListener('scroll', close, { capture: true });
            document.removeEventListener('mousedown', handleOutside, true);
            document.removeEventListener('touchstart', handleOutside, true);
        };
    }, [isMobile, open]);

    // Dismiss on a tap/click anywhere outside the icon. The bubble itself is
    // pointer-events:none, so a tap on it lands on whatever is underneath and
    // counts as outside — which is the intent. Taps on the icon are excluded so
    // the toggle below stays in charge of them (a capture-phase close here would
    // otherwise fire first and let the toggle immediately reopen it).
    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: PointerEvent) => {
            if (wrapRef.current?.contains(e.target as Node)) return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [open]);

    return (
        <span
            ref={wrapperRef}
            style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
            onMouseEnter={!isMobile ? handleMouseEnter : undefined}
            onMouseLeave={!isMobile ? () => setOpen(false) : undefined}
            onClick={isMobile ? handleMobileClick : undefined}
        >
            <svg
                ref={iconRef}
                width="10" height="10" viewBox="0 0 16 16" fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ cursor: isMobile ? 'pointer' : 'default', display: 'block' }}
            >
                <circle cx="8" cy="8" r="7" stroke="var(--t3, #9CA3B8)" strokeWidth="1.5" />
                <path d="M8 7.5v4" stroke="var(--t3, #9CA3B8)" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="8" cy="5.5" r="0.9" fill="var(--t3, #9CA3B8)" />
            </svg>
            {open && (
                <div
                    ref={tooltipRef}
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        left: pos.left,
                        transform: isRight ? 'translateY(-50%)' : 'translateY(-100%)',
                        width: 'max-content',
                        maxWidth: TOOLTIP_MAX_WIDTH,
                        background: 'var(--surface, #fff)',
                        border: '1px solid var(--card-border, #E2E8F0)',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 11,
                        color: 'var(--t2, #6B7280)',
                        lineHeight: 1.5,
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                        textTransform: 'none',
                        fontWeight: 400,
                        letterSpacing: 0,
                    }}
                >
                    {text}
                    <div style={isRight ? {
                        position: 'absolute',
                        left: -5,
                        top: pos.caretTop,
                        transform: 'translateY(-50%) rotate(45deg)',
                        width: 8,
                        height: 8,
                        background: 'var(--surface, #fff)',
                        borderLeft: '1px solid var(--card-border, #E2E8F0)',
                        borderBottom: '1px solid var(--card-border, #E2E8F0)',
                    } : {
                        position: 'absolute',
                        bottom: -5,
                        left: pos.caretLeft,
                        transform: 'translateX(-50%) rotate(45deg)',
                        width: 8,
                        height: 8,
                        background: 'var(--surface, #fff)',
                        borderRight: '1px solid var(--card-border, #E2E8F0)',
                        borderBottom: '1px solid var(--card-border, #E2E8F0)',
                    }} />
                </div>
            )}
        </span>
    );
};

export default KpiTooltip;
